@if (loading) {
    <spinner-overlay [fullscreen]="true"></spinner-overlay>
}
@if (organisation) {
    <div id="menu">
        <app-menu [organisation]="organisation" [linqset]="linqset" [project]="linqset?.project" [screen]="screen" (menuEvent)="handleFunctionEvent($event)" (selectNodeEvent)="handleSelectNodeEvent($event)"></app-menu>
    </div>

    @if (isHelpOn()) {
        <div class="hint">Provides analysis of the model data in the organisation. You can switch between current state, projects, models and organisation level data. You can also compare between projects and/or current state.</div>
    }

    <mat-sidenav-container class="drawer-container">
        <mat-sidenav #leftdrawer mode="side" [opened]="sidebarOpen" position="start" [style.width.px]="sidebarWidth" disableClose="true" class="filter-panel">
            @if (returnModelKey && startWorkspaceKey) {
                <button mat-stroked-button color="accent" class="return-button" (click)="returnToModel()"><mat-icon>arrow_back</mat-icon>Return to model</button>
            }

            <div id="filter">
                <section class="panel-section">
                    <app-group-selection #filterselection [groups]="filters" (selectedEvent)="selectFilter($event)" label="Saved filters" [multiple]="false" hint="Open a filter you or your colleagues have saved" placeholder="No saved filter selected"></app-group-selection>
                    @if (linqset) {
                        <div class="showing" [innerHTML]="linqset.description"></div>
                    }
                </section>

                @if (filter) {
                    <section class="panel-section">
                        <h3>Show</h3>
                        @if (isHelpOn()) {
                            <div class="hint">Choose the workspace, then narrow it down to particular models by name, function or tag.</div>
                        }
                        <mat-form-field color="accent">
                            <mat-label>Workspace</mat-label>
                            <mat-select [(value)]="filter.workspaceKey" (selectionChange)="onWorkspaceChange($event.value)">
                                @for (w of workspaces; track w._key) {
                                    <mat-option [value]="w._key">{{w.name}}</mat-option>
                                }
                            </mat-select>
                            <mat-hint>Current state or a project</mat-hint>
                        </mat-form-field>

                        @if (linqset && linqset.isProjectModel() && filter) {
                            <mat-slide-toggle [(ngModel)]="filter.comparison" (change)="toggleCompare()" class="toggle">Compare with current state</mat-slide-toggle>
                        }

                        <app-group-selection #modelselection [groups]="modelsData" (openedEvent)="onModelSelectionOpened($event)" label="Models" [multiple]="true" hint="Leave empty to see the whole organisation" placeholder="All models"></app-group-selection>
                    </section>

                    <section class="panel-section">
                        <h3>Focus on</h3>
                        @if (isHelpOn()) {
                            <div class="hint">Pick a type below to focus on one of its nodes. Captures include everything downstream, outcomes everything upstream.</div>
                        }

                        <app-node-types-insights [typeData]="typeData" [useEllipse]="true" (selectTypeEvent)="selectType($event)"></app-node-types-insights>

                        @if (groupedNodes && groupedNodes.length > 0) {
                            <app-group-selection #groupednodesselection [groups]="groupedNodes" (selectedEvent)="onGroupedNodeChange($event)" label="Nodes" [multiple]="false" [clearable]="true" placeholder="Select a node to filter on" hint="Show up/down stream nodes"></app-group-selection>
                        }@else if (nodes && nodes.length > 0) {
                            <mat-form-field color="accent">
                                <mat-label>{{getSuperTypeName()}}</mat-label>
                                <mat-select [(value)]="selectedNodes" (openedChange)="onStartingNodeChange($event)" (selectionChange)="onNodeSelectionChange($event)" [placeholder]="'Select a ' + getSuperTypeName()" multiple>
                                    <mat-select-trigger>{{getSelectedNodeNames()}}</mat-select-trigger>
                                    @for (n of nodes; track n._key) {
                                        <mat-option [value]="n">
                                            <div class="select-option-image">
                                                <app-node-svg [type]="n.type"/>
                                                <span>{{n.name}}</span>
                                            </div>
                                        </mat-option>
                                    }
                                </mat-select>
                                <mat-hint>{{getNodeSelectionHint()}}</mat-hint>
                            </mat-form-field>
                        }

                        <mat-form-field color="accent">
                            <mat-label>Information asset</mat-label>
                            <mat-select [(value)]="informationAssetKey" (selectionChange)="onInformationAssetChange($event.value)" placeholder="All assets">
                                @for (n of informationAssets; track n._key) {
                                    <mat-option [value]="n._key">{{n.name}}</mat-option>
                                }
                            </mat-select>
                            <mat-hint>Information using this asset, and the actions and systems around it</mat-hint>
                        </mat-form-field>

                        <app-group-selection #tagselection [groups]="tags" (openedEvent)="onTagSelectionOpened($event)" label="Tags" [multiple]="true" placeholder="Any tags" hint="Actions with these tags, and what they use"></app-group-selection>

                        <button mat-button color="accent" (click)="clearFilters()"><mat-icon>restart_alt</mat-icon>Reset</button>
                    </section>

                    <section class="panel-section">
                        <h3>{{isSavedFilter() ? 'Saved filter' : 'Save as a filter'}}</h3>
                        @if (!canChangeFilter()) {
                            <p class="note">This filter belongs to someone else. You can use it, but only its owner or an organisation admin can change it.</p>
                        }
                        <mat-form-field color="accent">
                            <mat-label>Filter name</mat-label>
                            <input matInput [(ngModel)]="filter.name" placeholder="For example Customer onboarding" [disabled]="!canChangeFilter()" maxlength="60">
                        </mat-form-field>
                        <mat-slide-toggle [(ngModel)]="filter.private" [disabled]="!canChangeFilter()" class="toggle">{{filter.private ? 'Private to me' : 'Shared with the organisation'}}</mat-slide-toggle>

                        <div class="button-row">
                            <button mat-flat-button color="accent" (click)="saveFilter()" [disabled]="!filter.name?.trim() || !canChangeFilter()">{{isSavedFilter() ? 'Save changes' : 'Save filter'}}</button>
                            @if (isSavedFilter()) {
                                <button mat-stroked-button [cdkCopyToClipboard]="filterUrl" (cdkCopyToClipboardCopied)="showConfirmation()" [disabled]="filter.private" [matTooltip]="filter.private ? 'Share the filter with the organisation to get a link' : 'Copy a link to this filter'"><mat-icon>link</mat-icon>Copy link</button>
                                <button mat-button color="warn" (click)="deleteFilter()" [disabled]="!canChangeFilter()"><mat-icon>delete</mat-icon>Delete</button>
                            }
                        </div>
                    </section>
                }
            </div>
        </mat-sidenav>
        <mat-sidenav-content>
            <div class="flex-container">
                <div class="flex-insights">
                    <button mat-icon-button class="panel-toggle" (click)="toggleSidebar()" [matTooltip]="sidebarOpen ? 'Hide filters' : 'Show filters'" [attr.aria-label]="sidebarOpen ? 'Hide filters' : 'Show filters'">
                        <mat-icon>{{sidebarOpen ? 'menu_open' : 'filter_list'}}</mat-icon>
                    </button>
                    <mat-tab-group #tab preserveContent class="insights-tabs">
                        <mat-tab selected>
                            <ng-template mat-tab-label>
                                <span class="material-symbols-outlined tab-icon">graph_2</span>
                                Model
                            </ng-template>
                            <div id="toolbar">
                                <app-graph-toolbar [organisation]="organisation" [linqset]="linqset" [graphComponent]="insightsGraphComponent?.graphComponent" [readOnly]="true" [screen]="screen" (toolbarEvent)="handleFunctionEvent($event)"></app-graph-toolbar>
                            </div>
                            <div class="graph-container">
                                <app-graph-component [linqset]="linqset" [trigger]="reloadTrigger" [modelType]="modelType" [readOnly]="true" (itemDoubleClickedEvent)="graphItemDoubleClickHandler($event)"></app-graph-component>
                            </div>
                        </mat-tab>
                        @if (linqset) {
                            <mat-tab>
                                <ng-template mat-tab-label>
                                    <span class="material-symbols-outlined tab-icon">overview</span>
                                    Overview
                                </ng-template>
                                @defer {
                                    <div class="card-container">
                                        <app-insights-overview #insightsOverview [graph]="insightsGraphComponent?.graphComponent?.graph" [linqset]="linqset" [comparison]="filter?.comparison && comparison" [trigger]="reloadTrigger" (setupCompletedEvent)="insightsCompleted()"></app-insights-overview>
                                    </div>
                                }
                            </mat-tab>
                            @if (linqset.hasModelNodes) {
                                <mat-tab>
                                    <ng-template mat-tab-label>
                                        <span class="material-symbols-outlined tab-icon">api</span>
                                        System interfaces
                                    </ng-template>

                                    @defer {
                                        <div class="card-container">
                                            <app-system-interfaces [organisation]="organisation" [linqset]="linqset"></app-system-interfaces>
                                        </div>
                                    }
                                </mat-tab>
                            }
                            <mat-tab>
                                <ng-template mat-tab-label>
                                    <span class="material-symbols-outlined tab-icon">graph_3</span>
                                    Nodes
                                </ng-template>

                                <div class="card-container">
                                    @defer {
                                        <app-node-table [linqset]="linqset" [trigger]="reloadTrigger" [comparison]="filter?.comparison && comparison" [organisationLinqset]="linqsetRepo.organisationLinqset" [graphComponent]="insightsGraphComponent?.graphComponent" [isGrouped]="false" [multiSelect]="false"></app-node-table>
                                    }
                                </div>
                            </mat-tab>                               
                            <mat-tab>
                                <ng-template mat-tab-label>
                                    <span class="material-symbols-outlined tab-icon">finance</span>
                                    EPM scorecard
                                </ng-template>
                                @defer {
                                    <app-epm-scorecard-tab [linqset]="linqset" [trigger]="reloadTrigger"></app-epm-scorecard-tab>
                                }
                            </mat-tab>
                            <mat-tab>
                                <ng-template mat-tab-label>
                                    <span class="material-symbols-outlined tab-icon">groups</span>
                                    Workforce
                                </ng-template>
                                @defer {
                                    <app-workforce-report [linqset]="linqset" [trigger]="reloadTrigger"></app-workforce-report>
                                }
                            </mat-tab>
                            <mat-tab>
                                <ng-template mat-tab-label>
                                    <span class="material-symbols-outlined tab-icon">dns</span>
                                    Systems
                                </ng-template>
                                @defer {
                                    <app-systems-report [linqset]="linqset" [trigger]="reloadTrigger"></app-systems-report>
                                }
                            </mat-tab>
                            <mat-tab>
                                <ng-template mat-tab-label>
                                    <span class="material-symbols-outlined tab-icon">wand_stars</span>
                                    AI Summary (Beta)
                                </ng-template>
                                
                                @defer {
                                    <div class="card-container">
                                        <app-query-ai [linqset]="linqset"></app-query-ai>
                                    </div>
                                }
                            </mat-tab>
                        }
                    </mat-tab-group>
                </div>
            </div>
        </mat-sidenav-content>
        <mat-sidenav #rightdrawer mode="side" [opened]="false" position="end" [style.width.px]="600">
            @defer {
                @if (insightsGraphComponent?.graphComponent && linqset) {
                    <app-temporal-settings [linqset]="linqset" [organisationLinqset]="linqsetRepo.organisationLinqset" [graphComponent]="insightsGraphComponent?.graphComponent" (closeEvent)="handleFunctionEvent({function:FUNCTION.TOGGLE_TEMPORAL_MODEL})"></app-temporal-settings>
                }
            }
        </mat-sidenav>
    </mat-sidenav-container>
}